import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { ReferralService } from '../referral.service';
import { StorageHelper } from '../helpers/StorageHelper';
import { Global } from '../classes/Global';
import { ValidationHelper } from '../helpers/ValidationHelper';
import Swal from 'sweetalert2';
import { DateHelper } from '../helpers/DateHelper';
import { Referral, ReferralData } from '../classes/IReferralInterfaces';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { InfoService } from '../info.service';
import { takeUntil } from 'rxjs/operators';
import { LoginHelper } from '../helpers/LoginHelper';
import { Config } from '../classes/Config';

@Component({
  selector: 'app-referrals',
  templateUrl: './referrals.component.html',
  styleUrls: ['./referrals.component.scss'],
  providers: [ ReferralService, AuthenticationService ]
})
export class ReferralsComponent extends BaseComponent implements OnInit {

  referralData: ReferralData;
  inviteEmail: string = "";
  invalidInviteEmail: boolean = false;

  isActivated = Config.activateReferrals;

  @ViewChild("authentication", {static: false}) authentication;

  constructor(
    private referralService: ReferralService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    protected infoService: InfoService,
  ) {
    super();
  }

  ngOnInit() {
    if(this.authService.isLoggedIn()) {
      this.getReferralData();
    }
    else {
      this.getSessionTokenFromUrl();
    }
  }

  getSessionTokenFromUrl() {
    //Notifier
    const ngUnsubscribe: Subject<any> = new Subject();

    this.route.queryParams.pipe(takeUntil(ngUnsubscribe.asObservable())) //take values until a notifier emits a value
    .subscribe(params => {
      //Check if there is a sessionToken in the url, if yes,
      //The user probably has been redirected on in the app,
      //Get the user data
      const sessionToken = params['sessionToken'];
      if (sessionToken) {
        this.infoService.getUserDataBySessionToken(sessionToken, Global.getCountry()).subscribe(result => {

          if(result["status"] == "error") {
            setTimeout(() => {
              this.authentication.openLogin();
            });
          }
          else {
            StorageHelper.getInstance().save(StorageHelper.keys.sessionToken, sessionToken);
            StorageHelper.getInstance().save(StorageHelper.keys.username, result['username']);
            StorageHelper.getInstance().save(StorageHelper.keys.email, result['mail']);
            StorageHelper.getInstance().save(StorageHelper.keys.profileImageUrl, result['profileImage']);
            StorageHelper.getInstance().save(StorageHelper.keys.uniqueCustomerId, result['uniqueCustomerId']);
            this.authService.setIsLoggedIn(true);
            LoginHelper.getInstance().loginChanged(true);
            this.getReferralData();
          }

        }, err => {
          setTimeout(() => {
            this.authentication.openLogin();
          });
        });
      }
      else {
        setTimeout(() => {
          this.authentication.openLogin();
        });
      }

      //notifies that params have been received and route.params can be unsubscribed
      ngUnsubscribe.next();
      ngUnsubscribe.complete();

    });

  }

  getReferralData() {
    this.referralService.getUserReferralData(
      StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
      Global.getCountry()
    ).subscribe((res: any) => {
      if(res && res.user) {
        this.referralData = res as ReferralData;
      }
    }, err => {

    });
  }

  sendInvitation() {

    if(ValidationHelper.isValidString(this.inviteEmail) && ValidationHelper.isValidEmail(this.inviteEmail)) {

      if((<any>window).gtag) {
        (<any>window).gtag('event', 'SendInvitation', {
          'event_category': 'Referral',
          'event_label': Global.getCountry()
        });
      }
      if ((<any>window)._paq) {
        (<any>window)._paq.push(['trackEvent', "Referral", "SendInvitation", Global.getCountry()]);
      }

      this.referralService.inviteUser(
        this.inviteEmail,
        StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
        Global.getCountry()).subscribe(res => {
          if(res && res["status"] == "success") {
            this.showSuccessPopup(this.getString("invitationMailSentSuccessfully"));
            this.getReferralData();
          }
          else {
            this.showWarningPopup(this.getString("invitationMailNotSent") + " " + res["message"]);
          }
          this.invalidInviteEmail = false;
          this.inviteEmail = "";
        }, err => {
          console.error(err);
          this.showErrorPopup(this.getString("serverError"));
        });
    }
    else {
      this.invalidInviteEmail = true;
    }
  }

  showSuccessPopup(message: string) {
    Swal.fire({
      title: '',
      icon: 'success',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  showWarningPopup(message: string) {
    Swal.fire({
      title: '',
      icon: 'warning',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonColor: "#00BAF7",
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  showErrorPopup(message: string) {
    Swal.fire({
      title: '',
      icon: 'error',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonColor: "#00BAF7",
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  resendInvitation(referral: Referral) {

    if(referral.referralStatus == 'WAITING_FOR_SIGNING_UP') {

      this.referralService.resendInvitationMail(
        referral.id.toString(),
        StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
        Global.getCountry()).subscribe(res => {
          if(res && res["status"] == "success") {
            this.showSuccessPopup(this.getString("invitationMailSentSuccessfully"));
            this.getReferralData();
          }
          else {
            this.showWarningPopup(this.getString("invitationMailNotSent") + " " + res["message"]);
          }
        }, err => {
          console.error(err);
          this.showErrorPopup(this.getString("serverError"));
        });

    }

  }

  removeInvitation(referral: Referral) {

    Swal.fire({
      title: this.getString("areYouSure"),
      text: this.getString("youWontBeAbleToRevertThis"),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00BAF7',
      cancelButtonColor: '#d33',
      cancelButtonText: this.getString("cancel"),
      confirmButtonText: this.getString("yesDeleteIt"),
      reverseButtons: true
    }).then((result) => {
      if (result.value) {

        this.referralService.remove(
          referral.id.toString(),
          StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
          Global.getCountry()).subscribe(res => {
            if(res && res["status"] == "success") {
              Swal.fire({
                title: this.getString("deleted"),
                text: this.getString("invitationDeleted"),
                icon: 'success',
                confirmButtonColor: "#00BAF7"
              }).then(() => {
                this.getReferralData();
              });
            }
            else {
              this.showErrorPopup(this.getString("serverError"));
            }
          }, err => {
            console.error(err);
            this.showErrorPopup(this.getString("serverError"));
          });

      }
    })

  }

  getFormattedDate(dateObj: any): string {
    return DateHelper.serverDateObjToDateString(dateObj);
  }

  facebookShareURL(): string {
    return "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(this.referralData.user.referralUrl);
  }

  twitterShareURL(): string {
    return "https://twitter.com/intent/tweet?text=" + encodeURIComponent(this.referralData.user.referralUrl);
  }

  emailShareURL(): string {
    return "mailto:?subject=" + this.getString("joinMeOnCheapCharts") + "&body=" + encodeURIComponent(this.referralData.user.referralUrl);
  }

  shareClick(label: string) {
    if((<any>window).gtag) {
      (<any>window).gtag('event', 'ShareClick', {
        'event_category': 'DetailView',
        'event_label': label
      });
    }
    if ((<any>window)._paq) {
      (<any>window)._paq.push(['trackEvent', "DetailView", "ShareClick", label]);
    }
  }

  selectInput(e) {
    this.shareClick('Clipboard');
    e.target.setSelectionRange(0, this.referralData.user.referralUrl.length);
    document.execCommand("copy");
    this.showClipboardToolTip();
  }

  showClipboardToolTip() {
    const tooltip = document.getElementById("myCPTooltip");
    tooltip.innerHTML = this.getString('copied');
  }

  linkOut() {
    const tooltip = document.getElementById("myCPTooltip");
    tooltip.innerHTML = this.getString('copyToClipboard');
  }

  shareIconMouseEnter() {
    const tooltip = document.getElementById("myCPTooltip");
    tooltip.style.visibility = "visible";
    tooltip.style.opacity = "1";
  }

  shareIconMouseLeave() {
    const tooltip = document.getElementById("myCPTooltip");
    tooltip.style.visibility = null;
    tooltip.style.opacity = null;
    this.linkOut();
  }

  shareIconClick() {
    this.shareClick('Clipboard');
    const input:any = document.getElementById("shareLinkInput");
    input.focus();
    input.setSelectionRange(0, this.referralData.user.referralUrl.length);
    document.execCommand("copy");
    this.showClipboardToolTip();
  }

  getRedeemUrl(): string {
    return '/' + Global.getCountry() + '/' + this.getString("redeemGiftCardUrl");
  }

}


